/**
 * Stream B — B18-B21: Calendar Integration
 *
 * B18: Google Calendar OAuth. B19: Outlook/Apple CalDAV.
 * B20: Calendar embed widget. B21: Timezone scheduling.
 * Real apiFetch. CSS vars only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { Calendar, Link2, Unlink, CheckCircle2, Globe, Clock } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface CalendarAccount {
  id: string;
  provider: "google" | "outlook" | "apple";
  email: string;
  status: "connected" | "error" | "expired";
  connectedAt: string;
  syncDirection: "two_way" | "one_way_read" | "one_way_write";
}

interface CalendarSettings {
  accounts: CalendarAccount[];
  defaultTimezone: string;
  bookingBufferMinutes: number;
  minNoticeHours: number;
  embedEnabled: boolean;
}

const PROVIDERS = [
  { id: "google" as const, name: "Google Calendar", description: "Two-way sync with Google Calendar" },
  { id: "outlook" as const, name: "Outlook / Microsoft 365", description: "Sync via Microsoft Graph API" },
  { id: "apple" as const, name: "Apple Calendar (CalDAV)", description: "Read-only sync via CalDAV" },
] as const;

export default function CalendarSyncPage() {
  const [settings, setSettings] = useState<CalendarSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setErrorMessage(null);
    try {
      const res = await apiFetch<{ data: CalendarSettings }>("/settings/calendar");
      setSettings(res.data);
    } catch {
      setSettings(null);
      setErrorMessage("Calendar settings could not be loaded from the live calendar sync service.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleConnect = async (provider: string) => {
    setIsConnecting(provider);
    setStatusMessage(null);
    setErrorMessage(null);
    try {
      const res = await apiFetch<{ data: { authUrl: string } }>("/settings/calendar/connect", {
        method: "POST",
        body: JSON.stringify({ provider }),
      });
      window.location.href = res.data.authUrl;
    } catch {
      setErrorMessage("Calendar authorization could not be started.");
      setIsConnecting(null);
    }
  };

  const handleDisconnect = async (accountId: string, email: string) => {
    const confirmed = window.confirm(`Disconnect ${email}? Calendar events will stop syncing.`);
    if (!confirmed) return;
    setStatusMessage(null);
    setErrorMessage(null);
    try {
      await apiFetch(`/settings/calendar/${accountId}`, { method: "DELETE" });
      await fetchSettings();
      setStatusMessage(`${email} was disconnected.`);
    } catch {
      setErrorMessage(`${email} could not be disconnected.`);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    setIsSaving(true);
    setStatusMessage(null);
    setErrorMessage(null);
    try {
      await apiFetch("/settings/calendar", { method: "PUT", body: JSON.stringify({ defaultTimezone: settings.defaultTimezone, bookingBufferMinutes: settings.bookingBufferMinutes, minNoticeHours: settings.minNoticeHours, embedEnabled: settings.embedEnabled }) });
      setStatusMessage("Calendar settings saved.");
    } catch {
      setErrorMessage("Calendar settings could not be saved.");
    }
    finally { setIsSaving(false); }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-20" />)}
      </div>
    );
  }
  if (!settings) {
    return (
      <div className="max-w-2xl rounded-xl border border-[var(--color-error)]/40 bg-[var(--color-error)]/10 p-4 text-sm text-[var(--color-error)]" role="alert">
        {errorMessage ?? "Calendar settings are unavailable."}
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-8">
      <div className="flex items-center gap-3"><Calendar className="h-5 w-5 text-[var(--color-primary)]" /><div><h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Calendar Integration</h1><p className="text-sm text-[var(--color-muted-foreground)]">Sync appointments with your calendar</p></div></div>
      {errorMessage && <p className="rounded-lg border border-[var(--color-error)]/40 bg-[var(--color-error)]/10 p-3 text-sm text-[var(--color-error)]" role="alert">{errorMessage}</p>}
      {statusMessage && <p className="rounded-lg border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 p-3 text-sm text-[var(--color-success)]" role="status">{statusMessage}</p>}

      {/* Connected accounts */}
      {settings.accounts.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-3">Connected Calendars</h2>
          <div className="space-y-2">{settings.accounts.map(a => (
            <div key={a.id} className="stagger-item flex items-center justify-between rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <div className="flex items-center gap-3"><CheckCircle2 className={`h-5 w-5 ${a.status === "connected" ? "text-[var(--color-success)]" : "text-[var(--color-error)]"}`} /><div><p className="text-sm font-medium text-[var(--color-foreground)]">{a.email}</p><p className="text-xs text-[var(--color-muted-foreground)] capitalize">{a.provider} | {a.syncDirection.replace(/_/g, " ")}</p></div></div>
              <button type="button" onClick={() => handleDisconnect(a.id, a.email)} className="text-[var(--color-muted-foreground)] hover:text-[var(--color-error)]"><Unlink className="h-4 w-4" /></button>
            </div>
          ))}</div>
        </section>
      )}

      {/* Add calendar */}
      <section>
        <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-3">Add Calendar</h2>
        <div className="space-y-2">{PROVIDERS.map(p => {
          const isConnected = settings.accounts.some(a => a.provider === p.id && a.status === "connected");
          return (
            <button type="button" key={p.id} disabled={isConnected || isConnecting === p.id} onClick={() => handleConnect(p.id)}
              className="stagger-item w-full flex items-center justify-between rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 hover:bg-[var(--color-surface)] text-left disabled:opacity-50">
              <div><p className="font-medium text-[var(--color-foreground)]">{p.name}</p><p className="text-xs text-[var(--color-muted-foreground)]">{p.description}</p></div>
              {isConnected ? <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" /> : isConnecting === p.id ? <Skeleton className="h-5 w-5 bg-[var(--color-primary)]/20" /> : <Link2 className="h-5 w-5 text-[var(--color-muted-foreground)]" />}
            </button>
          );
        })}</div>
      </section>

      {/* Scheduling settings */}
      <section>
        <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-3 flex items-center gap-2"><Clock className="h-5 w-5 text-[var(--color-muted-foreground)]" />Scheduling</h2>
        <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 space-y-4">
          <div>
            <label className="text-sm text-[var(--color-foreground)]">Default Timezone</label>
            <select value={settings.defaultTimezone} onChange={e => setSettings(current => current ? {...current, defaultTimezone: e.target.value} : current)} className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]">
              {(() => { try { return Intl.supportedValuesOf("timeZone"); } catch { return ["UTC","America/New_York","Europe/London","Europe/Berlin","Asia/Tokyo"]; } })().map(tz => <option key={tz} value={tz}>{tz.replace(/_/g," ")}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[var(--color-foreground)]">Buffer between bookings</label>
              <select value={settings.bookingBufferMinutes} onChange={e => { const v = Number(e.target.value); if (!Number.isNaN(v)) setSettings(current => current ? {...current, bookingBufferMinutes: v} : current); }} className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]">
                <option value={0}>No buffer</option><option value={5}>5 min</option><option value={10}>10 min</option><option value={15}>15 min</option><option value={30}>30 min</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-[var(--color-foreground)]">Minimum notice</label>
              <select value={settings.minNoticeHours} onChange={e => { const v = Number(e.target.value); if (!Number.isNaN(v)) setSettings(current => current ? {...current, minNoticeHours: v} : current); }} className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]">
                <option value={1}>1 hour</option><option value={2}>2 hours</option><option value={4}>4 hours</option><option value={12}>12 hours</option><option value={24}>24 hours</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[var(--color-foreground)]">
            <input type="checkbox" checked={settings.embedEnabled} onChange={e => setSettings(current => current ? {...current, embedEnabled: e.target.checked} : current)} className="rounded" />
            <Globe className="h-3.5 w-3.5" /> Enable calendar embed widget on booking pages
          </label>
          <button type="button" onClick={handleSaveSettings} disabled={isSaving} className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50">
            {isSaving ? <Skeleton className="h-4 w-20 bg-white/25" /> : "Save Settings"}
          </button>
        </div>
      </section>
    </div>
  );
}
